"use client"

import { useState } from "react"
import { Meeting } from "@/schema"
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@ui/shad"
import { Mic, Phone } from "lucide-react"

import { liveCaptureAllowed } from "@/lib/live-capture"
import { useBookNotetakerPolicy } from "@/hooks/api/notetaker-policies"
import { CallMyPhoneDialog } from "@/components/meetings/call-my-phone-dialog"
import { blockedBy } from "@/components/notetaker-policy/policy-resolution"

interface RecordMeetingDropdownProps {
  meeting: Meeting
  onRecordOnWeb: (e: React.MouseEvent<HTMLElement>) => void
  size?: "sm" | "default"
  variant?: "primary" | "outline"
  disabled?: boolean
  recordLabel?: string
}

export function RecordMeetingDropdown({
  meeting,
  onRecordOnWeb,
  size = "sm",
  variant = "primary",
  disabled,
  recordLabel = "Record",
}: RecordMeetingDropdownProps) {
  const [callMyPhoneOpen, setCallMyPhoneOpen] = useState(false)
  const { data: notetakerPolicy } = useBookNotetakerPolicy(meeting.book_id ?? "")
  const phoneRecordingBlocked = Boolean(blockedBy(notetakerPolicy, "phone_recording"))
  const canRecordOnWeb = liveCaptureAllowed(meeting)

  const householdId = meeting.households?.[0]?.id

  if (!canRecordOnWeb && phoneRecordingBlocked) return null

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size={size} variant={variant} disabled={disabled} onClick={(e) => e.stopPropagation()}>
            <Mic />
            {recordLabel}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canRecordOnWeb && (
            <DropdownMenuItem onClick={onRecordOnWeb} className="hidden sm:flex">
              <Mic className="text-icon size-3.5!" />
              Record on web
            </DropdownMenuItem>
          )}
          {!phoneRecordingBlocked && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                setCallMyPhoneOpen(true)
              }}
            >
              <Phone className="text-icon size-3.5!" />
              Call my phone
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <CallMyPhoneDialog
        open={callMyPhoneOpen}
        onOpenChange={setCallMyPhoneOpen}
        meetingId={meeting.id}
        householdId={householdId}
        title={meeting.title}
      />
    </>
  )
}
